import { supabase } from '../lib/supabase';

const LEVELS = ['overview', 'level_1', 'level_2', 'level_3', 'level_4', 'level_5'];
const LEVEL_LABELS = {
  overview: 'Overview',
  level_1: 'Level 1',
  level_2: 'Level 2',
  level_3: 'Level 3',
  level_4: 'Level 4',
  level_5: 'Level 5',
};

/**
 * Track a video watch event (milestone or completion)
 */
export const trackWatchEvent = async ({
  videoId,
  songAnalysisId = null,
  lessonLevel = 'overview',
  watchDurationSeconds = 0,
  totalDurationSeconds = 0,
  completed = false,
  replayCount = 0,
  watchPercentage = 0,
  milestone = null,
}) => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return { error: 'Not authenticated' };

    const { data, error } = await supabase?.from('video_watch_events')?.insert({
      user_id: user?.id,
      video_id: videoId,
      song_analysis_id: songAnalysisId || null,
      lesson_level: lessonLevel,
      watch_duration_seconds: Math.round(watchDurationSeconds),
      total_duration_seconds: Math.round(totalDurationSeconds),
      completed,
      replay_count: replayCount,
      watch_percentage: watchPercentage,
      milestone,
      watched_at: new Date()?.toISOString(),
    });

    if (error) {
      console.error('Error tracking watch event:', error);
      return { error };
    }
    return { data };
  } catch (err) {
    console.error('trackWatchEvent error:', err);
    return { error: err };
  }
};

/**
 * Get summary statistics for the admin dashboard
 */
export const getVideoEngagementSummary = async (dateRange = 30) => {
  try {
    const since = new Date();
    since?.setDate(since?.getDate() - dateRange);

    const { data, error } = await supabase
      ?.from('video_watch_events')
      ?.select('*')
      ?.gte('watched_at', since?.toISOString());

    if (error) throw error;

    const events = data || [];
    const totalWatchSeconds = events?.reduce((sum, e) => sum + (e?.watch_duration_seconds || 0), 0);
    const completionEvents = events?.filter(e => e?.completed);
    const uniqueVideoUsers = new Set(events.map(e => `${e.video_id}_${e.user_id}`));
    const completedVideoUsers = new Set(completionEvents.map(e => `${e.video_id}_${e.user_id}`));
    const avgCompletionRate = uniqueVideoUsers?.size > 0
      ? Math.round((completedVideoUsers?.size / uniqueVideoUsers?.size) * 100)
      : 0;
    const totalReplays = events?.reduce((sum, e) => sum + (e?.replay_count || 0), 0);
    const activeLearners = new Set(events.map(e => e.user_id))?.size;

    // Avg watch time per event
    const avgWatchSeconds = events?.length > 0
      ? Math.round(totalWatchSeconds / events?.length)
      : 0;

    // Replay rate = events with replay_count > 0 / total events
    const replayRate = events?.length > 0
      ? Math.round((events?.filter(e => (e?.replay_count || 0) > 0)?.length / events?.length) * 100)
      : 0;

    // Drop-off point: avg watch_percentage across non-completed events
    const nonCompleted = events?.filter(e => !e?.completed);
    const avgDropOffPct = nonCompleted?.length > 0
      ? Math.round(nonCompleted?.reduce((s, e) => s + (e?.watch_percentage || 0), 0) / nonCompleted?.length)
      : 0;

    return {
      data: {
        totalWatchSeconds,
        totalWatchMinutes: Math.round(totalWatchSeconds / 60),
        avgCompletionRate,
        totalReplays,
        activeLearners,
        totalEvents: events?.length,
        avgWatchSeconds,
        replayRate,
        avgDropOffPct,
      }
    };
  } catch (err) {
    console.error('getVideoEngagementSummary error:', err);
    return { error: err };
  }
};

/**
 * Get engagement breakdown by lesson level (for bar chart)
 */
export const getEngagementByLevel = async (dateRange = 30) => {
  try {
    const since = new Date();
    since?.setDate(since?.getDate() - dateRange);

    const { data, error } = await supabase
      ?.from('video_watch_events')
      ?.select('lesson_level, watch_percentage, completed, user_id, video_id, watch_duration_seconds')
      ?.gte('watched_at', since?.toISOString());

    if (error) throw error;

    const events = data || [];

    const byLevel = LEVELS?.map(level => {
      const levelEvents = events?.filter(e => e?.lesson_level === level);
      const avgWatchPct = levelEvents?.length > 0
        ? Math.round(levelEvents?.reduce((sum, e) => sum + (e?.watch_percentage || 0), 0) / levelEvents?.length)
        : 0;
      const completions = levelEvents?.filter(e => e?.completed)?.length;
      const completionRate = levelEvents?.length > 0
        ? Math.round((completions / levelEvents?.length) * 100)
        : 0;
      const uniqueViewers = new Set(levelEvents.map(e => e.user_id))?.size;
      const avgWatchSecs = levelEvents?.length > 0
        ? Math.round(levelEvents?.reduce((s, e) => s + (e?.watch_duration_seconds || 0), 0) / levelEvents?.length)
        : 0;

      // Drop-off: avg watch_percentage of non-completed events
      const nonCompleted = levelEvents?.filter(e => !e?.completed);
      const dropOffPct = nonCompleted?.length > 0
        ? Math.round(nonCompleted?.reduce((s, e) => s + (e?.watch_percentage || 0), 0) / nonCompleted?.length)
        : null;

      return {
        level,
        label: LEVEL_LABELS?.[level],
        avgWatchPercentage: avgWatchPct,
        completionRate,
        totalViews: levelEvents?.length,
        uniqueViewers,
        avgWatchSeconds: avgWatchSecs,
        dropOffPct,
      };
    });

    return { data: byLevel };
  } catch (err) {
    console.error('getEngagementByLevel error:', err);
    return { error: err };
  }
};

/**
 * Get heat map data: completion rates per song per level
 * Returns songs as rows, levels as columns, with completion rate per cell
 */
export const getHeatMapData = async (dateRange = 30) => {
  try {
    const since = new Date();
    since?.setDate(since?.getDate() - dateRange);

    // Fetch watch events with video info joined
    const { data: watchEvents, error: watchError } = await supabase
      ?.from('video_watch_events')
      ?.select('video_id, lesson_level, completed, watch_percentage')
      ?.gte('watched_at', since?.toISOString());

    if (watchError) throw watchError;

    // Fetch all videos with song info
    const { data: videos, error: videosError } = await supabase
      ?.from('videos')
      ?.select('id, title, song_id, level, published, songs(title)')
      ?.eq('published', true);

    if (videosError) throw videosError;

    const allVideos = videos || [];
    const allEvents = watchEvents || [];

    // Build a map: songId -> { songTitle, levels: { level: { videoId, completionRate, totalViews, hasVideo } } }
    const songMap = {};

    // First populate from videos table (coverage)
    allVideos?.forEach(v => {
      const songId = v?.song_id;
      if (!songId) return;
      const songTitle = v?.songs?.title || v?.title || 'Unknown Song';
      if (!songMap?.[songId]) {
        songMap[songId] = { songId, songTitle, levels: {} };
      }
      const lvl = v?.level || 'overview';
      songMap[songId].levels[lvl] = {
        videoId: v?.id,
        hasVideo: true,
        completionRate: 0,
        totalViews: 0,
        avgWatchPct: 0,
      };
    });

    // Then overlay watch event stats
    allEvents?.forEach(e => {
      // Find which song this video belongs to
      const video = allVideos?.find(v => v?.id === e?.video_id);
      if (!video?.song_id) return;
      const songId = video?.song_id;
      const lvl = e?.lesson_level || video?.level || 'overview';

      if (!songMap?.[songId]) {
        songMap[songId] = {
          songId,
          songTitle: video?.songs?.title || video?.title || 'Unknown',
          levels: {},
        };
      }
      if (!songMap?.[songId]?.levels?.[lvl]) {
        songMap[songId].levels[lvl] = { videoId: e?.video_id, hasVideo: false, completionRate: 0, totalViews: 0, avgWatchPct: 0, _events: [] };
      }
      if (!songMap?.[songId]?.levels?.[lvl]?._events) {
        songMap[songId].levels[lvl]._events = [];
      }
      songMap?.[songId]?.levels?.[lvl]?._events?.push(e);
    });

    // Compute completion rates from _events
    Object.values(songMap)?.forEach(song => {
      LEVELS?.forEach(lvl => {
        const cell = song?.levels?.[lvl];
        if (!cell) return;
        const evts = cell?._events || [];
        if (evts?.length > 0) {
          const completions = evts?.filter(e => e?.completed)?.length;
          cell.completionRate = Math.round((completions / evts?.length) * 100);
          cell.totalViews = evts?.length;
          cell.avgWatchPct = Math.round(evts?.reduce((s, e) => s + (e?.watch_percentage || 0), 0) / evts?.length);
        }
        delete cell?._events;
      });
    });

    // Convert to array, sort by song title
    const rows = Object.values(songMap)?.sort((a, b) => a?.songTitle?.localeCompare(b?.songTitle));

    return { data: rows };
  } catch (err) {
    console.error('getHeatMapData error:', err);
    return { error: err };
  }
};

/**
 * Get coverage gaps: songs missing videos for specific levels
 */
export const getCoverageGaps = async () => {
  try {
    // Get all songs
    const { data: songs, error: songsError } = await supabase
      ?.from('songs')
      ?.select('id, title')
      ?.order('title');

    if (songsError) throw songsError;

    // Get all published videos
    const { data: videos, error: videosError } = await supabase
      ?.from('videos')
      ?.select('id, song_id, level, published, status');

    if (videosError) throw videosError;

    const allSongs = songs || [];
    const allVideos = videos || [];

    // Build coverage map
    const coverageMap = {};
    allVideos?.forEach(v => {
      if (!v?.song_id) return;
      if (!coverageMap?.[v?.song_id]) coverageMap[v?.song_id] = {};
      const lvl = v?.level || 'overview';
      coverageMap[v?.song_id][lvl] = {
        hasVideo: true,
        published: v?.published,
        status: v?.status,
      };
    });

    // Find gaps
    const rows = allSongs?.map(song => {
      const coverage = coverageMap?.[song?.id] || {};
      const missingLevels = LEVELS?.filter(lvl => !coverage?.[lvl]);
      const coveredLevels = LEVELS?.filter(lvl => !!coverage?.[lvl]);
      const coverageCount = coveredLevels?.length;
      const coveragePct = Math.round((coverageCount / LEVELS?.length) * 100);

      return {
        songId: song?.id,
        songTitle: song?.title,
        coverageCount,
        coveragePct,
        missingLevels,
        coveredLevels,
        levelStatus: LEVELS?.reduce((acc, lvl) => {
          acc[lvl] = coverage?.[lvl] || null;
          return acc;
        }, {}),
      };
    })?.filter(row => row?.missingLevels?.length > 0); // only songs with gaps

    // Sort by most missing first
    rows?.sort((a, b) => a?.coverageCount - b?.coverageCount);

    return { data: rows };
  } catch (err) {
    console.error('getCoverageGaps error:', err);
    return { error: err };
  }
};

/**
 * Get trending data: watch events and completion rates over time
 */
export const getTrendingData = async (dateRange = 30) => {
  try {
    const since = new Date();
    since?.setDate(since?.getDate() - dateRange);

    const { data, error } = await supabase
      ?.from('video_watch_events')
      ?.select('watched_at, completed, watch_percentage')
      ?.gte('watched_at', since?.toISOString())
      ?.order('watched_at', { ascending: true });

    if (error) throw error;

    const events = data || [];

    // Group by date
    const dateMap = {};
    events?.forEach(e => {
      const date = e?.watched_at?.split('T')?.[0];
      if (!date) return;
      if (!dateMap?.[date]) {
        dateMap[date] = { date, watchEvents: 0, completions: 0, totalPct: 0 };
      }
      dateMap[date].watchEvents += 1;
      if (e?.completed) dateMap[date].completions += 1;
      dateMap[date].totalPct += (e?.watch_percentage || 0);
    });

    // Fill in missing dates
    const result = [];
    const current = new Date(since);
    const now = new Date();
    while (current <= now) {
      const dateStr = current?.toISOString()?.split('T')?.[0];
      const entry = dateMap?.[dateStr] || { date: dateStr, watchEvents: 0, completions: 0, totalPct: 0 };
      const completionRate = entry?.watchEvents > 0
        ? Math.round((entry?.completions / entry?.watchEvents) * 100)
        : 0;
      result?.push({
        date: dateStr,
        watchEvents: entry?.watchEvents,
        completionRate,
        label: new Date(dateStr)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      });
      current?.setDate(current?.getDate() + 1);
    }

    return { data: result };
  } catch (err) {
    console.error('getTrendingData error:', err);
    return { error: err };
  }
};

/**
 * Get per-video performance metrics
 */
export const getVideoPerformanceTable = async (dateRange = 30) => {
  try {
    const since = new Date();
    since?.setDate(since?.getDate() - dateRange);

    const { data, error } = await supabase
      ?.from('video_watch_events')
      ?.select(`
        video_id,
        lesson_level,
        watch_duration_seconds,
        total_duration_seconds,
        completed,
        replay_count,
        user_id,
        song_analysis_id,
        character_song_analyses(
          song_id,
          role_id,
          songs(title),
          roles(character_name)
        )
      `)
      ?.gte('watched_at', since?.toISOString());

    if (error) throw error;

    const events = data || [];
    const videoMap = {};
    events?.forEach(e => {
      const key = e?.video_id;
      if (!videoMap?.[key]) {
        videoMap[key] = {
          videoId: e?.video_id,
          lessonLevel: e?.lesson_level,
          songTitle: e?.character_song_analyses?.songs?.title || 'Unknown Song',
          characterName: e?.character_song_analyses?.roles?.character_name || '',
          events: [],
        };
      }
      videoMap?.[key]?.events?.push(e);
    });

    const rows = Object.values(videoMap)?.map(v => {
      const evts = v?.events;
      const totalViews = evts?.length;
      const completions = evts?.filter(e => e?.completed)?.length;
      const completionRate = totalViews > 0 ? Math.round((completions / totalViews) * 100) : 0;
      const avgWatchSecs = totalViews > 0
        ? Math.round(evts?.reduce((s, e) => s + (e?.watch_duration_seconds || 0), 0) / totalViews)
        : 0;
      const totalReplays = evts?.reduce((s, e) => s + (e?.replay_count || 0), 0);
      const uniqueViewers = new Set(evts.map(e => e.user_id))?.size;

      return {
        videoId: v?.videoId,
        songTitle: v?.songTitle,
        characterName: v?.characterName,
        lessonLevel: v?.lessonLevel,
        totalViews,
        uniqueViewers,
        completionRate,
        avgWatchSeconds: avgWatchSecs,
        totalReplays,
        engagementScore: Math.round((completionRate * 0.6) + (Math.min(totalViews / 10, 40))),
      };
    });

    rows?.sort((a, b) => b?.totalViews - a?.totalViews);
    return { data: rows };
  } catch (err) {
    console.error('getVideoPerformanceTable error:', err);
    return { error: err };
  }
};

/**
 * Get top and bottom performing LEVELS by avg completion rate
 */
export const getTopBottomPerformers = async (dateRange = 30) => {
  try {
    const { data: levelData, error } = await getEngagementByLevel(dateRange);
    if (error) throw error;

    // Filter levels that have any views
    const withData = (levelData || [])?.filter(l => l?.totalViews > 0);
    const sorted = [...withData]?.sort((a, b) => b?.completionRate - a?.completionRate);
    const top = sorted?.slice(0, 3);
    const bottom = [...sorted]?.reverse()?.slice(0, 3);

    return { data: { top, bottom } };
  } catch (err) {
    console.error('getTopBottomPerformers error:', err);
    return { error: err };
  }
};

/**
 * Format seconds to human-readable duration
 */
export const formatDuration = (seconds) => {
  if (!seconds || seconds === 0) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s?.toString()?.padStart(2, '0')}`;
};

export const LEVEL_LABELS_MAP = LEVEL_LABELS;
export const LEVELS_LIST = LEVELS;
